import { ArrowDownIcon } from '@heroicons/react/outline';
import { ConfigProvider, Drawer } from 'antd';
import useBreakpoint from 'antd/lib/grid/hooks/useBreakpoint';
import StarWithoutBg from 'assets/Icons/StarWithoutBg';
import clsx from 'clsx';
import { AnimatePresence, motion } from 'framer-motion';
import Head from 'next/head';
import { useRouter } from 'next/router';
import React, { useEffect, useRef, useState } from 'react';
import { BiChevronLeft, BiHistory } from 'react-icons/bi';
import { useRecoilValue } from 'recoil';
import { twMerge } from 'tailwind-merge';

import { EventDetails, logEvent } from '@/hooks/useClickEventTracking';

import RenderIf from '@/components/Common/RenderIf';
import { triggerSessionEvent } from '@/components/Layout/ProductsHeader';
import { ShouldRender } from '@/components/shared/basic/ShouldRender';
import { formateErrorObject } from '@/components/shared/formateErrorObject';
import { notify } from '@/components/shared/notify';
import AnimatedBackground from '@/components/ZAIChatLayout/ChatInput/Animation/AnimatedBackground';
import ChatInput from '@/components/ZAIChatLayout/ChatInput/ChatInput';
import ChatMessages, {
  ChatMessage,
  ISourceDocuments,
} from '@/components/ZAIChatLayout/ChatMessages';
import SidebarChats, {
  ChatItem,
} from '@/components/ZAIChatLayout/SidebarChats';
import SourceDrawer from '@/components/ZAIChatLayout/SourceDrawer';
import Sources from '@/components/ZAIChatLayout/Sources';
import Suggestions from '@/components/ZAIChatLayout/Suggestions';

import { user } from '@/atoms/user';
import {
  askQuestionStream,
  getPrompts,
  getSessionMessages,
  sendResponseFeedback,
} from '@/services/AiChat';
import { User } from '@/utils/interfaces/User';

interface ZAIChatDrawerProps {
  open: boolean;
  onClose: () => void;
  bookId?: string;
  initialSessionId?: string;
}

const extractSessionId = (messageId: string): string | null => {
  if (!messageId) return null;
  const parts = messageId.split('-');
  return parts.length >= 3 && parts[0] ? parts[0] : null;
};

const extractQueryId = (messageId: string): string | null => {
  if (!messageId) return null;
  const parts = messageId.split('-');
  return parts.length >= 3 && parts[1] ? parts[1] : null;
};

const makeMessageId = (
  sessionId: string,
  sourceId: string,
  role: 'user' | 'assistant',
) => `${sessionId}-${sourceId}-${role}`;

const ZAI: React.FC<ZAIChatDrawerProps> = ({
  open,
  bookId,
  initialSessionId,
}) => {
  const [chats, setChats] = useState<ChatItem[]>([]);
  const [activeChatId, setActiveChatId] = useState('new');
  const [messagesMap, setMessagesMap] = useState<Record<string, ChatMessage[]>>(
    {},
  );
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [prompts, setPrompts] = useState<string[]>([]);
  const [promptsLoading, setPromptsLoading] = useState(false);
  const [messagesPageMap, setMessagesPageMap] = useState<
    Record<string, number>
  >({});
  const [messagesHasMoreMap, setMessagesHasMoreMap] = useState<
    Record<string, boolean>
  >({});
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  const [prompt, setPrompt] = useState('');
  const [processing, setProcessing] = useState(false);
  const [showScrollDown, setShowScrollDown] = useState(false);
  const [showSources, setShowSources] = useState(false);

  const [streamStatus, setStreamStatus] = useState<string | null>(null);
  const [streamAnswer, setStreamAnswer] = useState('');
  const [streamSources, setStreamSources] = useState<any[]>([]);
  const [, setFinalMessageData] = useState<any | null>(null);

  const [sourceDocumentsToDisplay, setSourceDocumentsToDisplay] = useState<
    ISourceDocuments[]
  >([]);

  const finalMessageRef = useRef<any>(null);
  const streamSourceRef = useRef<any>(null);

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const lastUpdateSourceRef = useRef<'user' | 'assistant' | null>(null);
  const prevScrollHeightRef = useRef(0);

  const checkScrollPosition = () => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const distanceFromBottom =
      container.scrollHeight - container.scrollTop - container.clientHeight;

    const hasOverflow = container.scrollHeight > container.clientHeight;

    const isAtBottom = distanceFromBottom <= 10; // threshold
    const shouldShow = hasOverflow && !isAtBottom;

    setShowScrollDown(shouldShow);
  };
  const screens = useBreakpoint();
  const ZAI_SESSION_KEY = 'zai_screen_session';

  const userData: User | undefined = useRecoilValue(user);
  const messages = messagesMap[activeChatId] || [];
  const router = useRouter();

  const isTempChat = (id?: string) => id?.startsWith('new-');

  const handleMessageFeedback = async (
    messageId: string,
    value: -1 | 0 | 1,
  ) => {
    const msgs = messagesMap[activeChatId];
    if (!msgs) return;

    const target = msgs.find((m) => m.id === messageId);
    if (!target || target.feedback === value) return;

    const sessionId = extractSessionId(messageId);
    const queryId = extractQueryId(messageId);
    if (!sessionId || !queryId) return;

    const questionIndex = msgs.findIndex((m) => m.id === messageId);
    const question =
      msgs[questionIndex - 1]?.role === 'user'
        ? msgs[questionIndex - 1]?.content
        : null;

    // ✅ optimistic UI update
    setMessagesMap((prev) => ({
      ...prev,
      [activeChatId]: (prev[activeChatId] ?? []).map((m) =>
        m.id === messageId ? { ...m, feedback: value } : m,
      ),
    }));

    try {
      await sendResponseFeedback(queryId, value);

      logEvent({
        eventDetails: {
          event_name:
            value === 1
              ? 'like'
              : value === -1
                ? 'dislike'
                : 'ask_ai_feedback_reset',
          source: 'ask_ai',
          source_type: 'global',
          source_id: sessionId,
          sub_source: target.content,
          sub_source_id: queryId,
          unit: 'boolean',
          value: 1,
          meta: {
            click_source: 'ask_ai_global_screen',
            session_id: sessionId,
            ai_query: question,
          },
        },
        userData,
        pathname: router.pathname,
      });
    } catch {
      // rollback on failure
      setMessagesMap((prev) => ({
        ...prev,
        [activeChatId]: (prev[activeChatId] ?? []).map((m) =>
          m.id === messageId ? { ...m, feedback: 0 } : m,
        ),
      }));
    }
  };

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const heightDiff = container.scrollHeight - prevScrollHeightRef.current;

    if (lastUpdateSourceRef.current === 'user' && heightDiff < 600) {
      bottomRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'end',
      });
    }

    prevScrollHeightRef.current = container.scrollHeight;
  }, [messages]);

  useEffect(() => {
    if (!open) return;
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [open]);

  const handleNewChat = () => {
    const existingTemp = chats.find((c) => isTempChat(c.id));
    if (existingTemp) {
      setActiveChatId(existingTemp.id);
      return;
    }

    const id = `new-${crypto.randomUUID()}`;

    setChats((prev) => [
      { id, title: 'New Chat', updatedAt: 'Today' },
      ...prev,
    ]);

    setMessagesMap((prev) => ({
      ...prev,
      [id]: [],
    }));

    setActiveChatId(id);
  };

  const fetchSessionMessages = async (
    sessionId: string,
    page = 1,
    append = false,
  ) => {
    if (sessionId.startsWith('new')) return;
    if (loadingHistory) return;

    try {
      setLoadingHistory(true);

      const res = await getSessionMessages(sessionId, page, 20);
      const list = res?.data ?? [];
      const hasMore = res?.pagination?.has_next ?? false;

      const mapped: ChatMessage[] = list.reverse().flatMap((m: any) => {
        const messages: ChatMessage[] = [];

        if (m.query) {
          messages.push({
            id: makeMessageId(sessionId, m.id, 'user'),
            role: 'user',
            content: m.query,
            feedback: m.feedback ?? 0,
            sourceDocuments: m.source_documents,
          });
        }

        if (m.result) {
          messages.push({
            id: makeMessageId(sessionId, m.id, 'assistant'),
            role: 'assistant',
            content: m.result,
            feedback: m.feedback ?? 0,
            sourceDocuments: m.source_documents,
          });
        }

        return messages;
      });

      setMessagesMap((prev) => ({
        ...prev,
        [sessionId]: append ? [...mapped, ...(prev[sessionId] ?? [])] : mapped,
      }));

      setMessagesPageMap((prev) => ({
        ...prev,
        [sessionId]: page,
      }));

      setMessagesHasMoreMap((prev) => ({
        ...prev,
        [sessionId]: hasMore,
      }));
    } catch (err: any) {
      notify(formateErrorObject(err), 'error');
    } finally {
      setLoadingHistory(false);
    }
  };

  const fetchPrompts = async () => {
    try {
      setPromptsLoading(true);

      const data = await getPrompts();
      const list = Array.isArray(data) ? data.map((p: any) => p.text ?? p) : [];

      setPrompts(list);
    } catch (error) {
      setPrompts([]);
    } finally {
      setPromptsLoading(false);
    }
  };

  const logAiQuery = (query: string) => {
    const eventParams: EventDetails = {
      event_name: 'ai_query',
      source: 'ask_ai',
      source_type: 'global',
      source_id: null,
      sub_source: null,
      sub_source_id: null,
      unit: 'boolean',
      value: 1,
      meta: { click_source: 'ask_ai_global_screen', ai_query: query },
    };

    logEvent({
      eventDetails: eventParams,
      userData,
      pathname: router.pathname,
    });
  };

  useEffect(() => {
    const handleUnload = () => {
      if (typeof localStorage === 'undefined') return;
      const raw = localStorage.getItem(ZAI_SESSION_KEY);
      if (!raw) return;

      try {
        const { start } = JSON.parse(raw);
        if (!start) return;

        triggerSessionEvent(
          new Date(start),
          new Date(),
          userData as User,
          router.pathname,
        );
      } catch {
        // ignore
      } finally {
        localStorage.removeItem(ZAI_SESSION_KEY);
      }
    };

    window.addEventListener('beforeunload', handleUnload);

    return () => {
      window.removeEventListener('beforeunload', handleUnload);
    };
  }, []);

  useEffect(() => {
    fetchPrompts();
  }, []);

  const safeArray = <T,>(value: T[] | undefined) =>
    Array.isArray(value) ? value : [];

  const handleSend = async ({ text }: { text?: string } = {}) => {
    const finalPrompt = prompt || text || '';

    if (!finalPrompt.trim() || processing) return;

    let currentChatId = activeChatId;

    if (currentChatId === 'new') {
      const existingTemp = chats.find((c) => isTempChat(c.id));

      if (existingTemp) {
        currentChatId = existingTemp.id;
        setActiveChatId(existingTemp.id);
      } else {
        const id = `new-${crypto.randomUUID()}`;

        setChats((prev) => [
          { id, title: 'New Chat', updatedAt: 'Today' },
          ...prev,
        ]);

        setMessagesMap((prev) => ({
          ...prev,
          [id]: [],
        }));

        currentChatId = id;
        setActiveChatId(id);
      }
    }

    const tempId = crypto.randomUUID();

    const userMsg: ChatMessage = {
      id: makeMessageId(currentChatId, tempId, 'user'),
      role: 'user',
      content: finalPrompt,
      feedback: 0,
      sourceDocuments: [],
    };

    lastUpdateSourceRef.current = 'user';

    setMessagesMap((prev) => ({
      ...prev,
      [currentChatId]: [...safeArray(prev[currentChatId]), userMsg],
    }));

    logAiQuery(userMsg.content);
    setPrompt('');
    setProcessing(true);

    try {
      setProcessing(true);
      setStreamStatus(null);
      setStreamAnswer('');
      setStreamSources([]);
      setFinalMessageData(null);

      await askQuestionStream(
        {
          question: userMsg.content,
          ...(currentChatId &&
          !currentChatId.startsWith('new') &&
          currentChatId !== '1'
            ? { session_id: currentChatId }
            : {}),
          ...(bookId ? { book_id: bookId } : {}),
          streaming: true,
        },
        (event) => {
          switch (event.type) {
            case 'status':
              setStreamStatus(event.data.message);
              break;

            case 'source_documents':
              streamSourceRef.current = event.data;
              setStreamSources(event.data);
              break;

            case 'message':
              setStreamAnswer((prev) => prev + event.data);
              break;

            case 'message_data':
              finalMessageRef.current = event.data;
              setFinalMessageData(event.data);
              break;

            case 'DONE': {
              const data = finalMessageRef.current;

              // if (data) {
              //   const aiMsg: ChatMessage = {
              //     id: makeMessageId(data.session_id, data.id, 'assistant'),
              //     role: 'assistant',
              //     content: data.result,
              //     feedback: data.feedback ?? 0,
              //   };

              //   setMessagesMap((prev) => ({
              //     ...prev,
              //     [data.session_id]: [
              //       ...safeArray(prev[data.session_id]),
              //       aiMsg,
              //     ],
              //   }));
              // }

              // setProcessing(false);
              // setStreamStatus(null);
              // setStreamAnswer('');

              if (data) {
                const resolvedSessionId = data.session_id || currentChatId;
                const wasTemp = currentChatId.startsWith('new-');

                if (wasTemp && resolvedSessionId) {
                  setChats((prev) =>
                    prev.map((c) =>
                      c.id === currentChatId
                        ? {
                            id: resolvedSessionId,
                            title: userMsg.content.slice(0, 40),
                            updatedAt: 'Today',
                          }
                        : c,
                    ),
                  );

                  setMessagesMap((prev) => ({
                    ...prev,
                    [resolvedSessionId]: safeArray(prev[currentChatId]),
                  }));

                  setActiveChatId(resolvedSessionId);
                }

                const aiMsg: ChatMessage = {
                  id: makeMessageId(resolvedSessionId, data.id, 'assistant'),
                  role: 'assistant',
                  content: data.result,
                  feedback: data.feedback ?? 0,
                  sourceDocuments: streamSourceRef.current,
                };

                lastUpdateSourceRef.current = 'assistant';

                setMessagesMap((prev) => ({
                  ...prev,
                  [resolvedSessionId]: [
                    ...safeArray(prev[resolvedSessionId]),
                    aiMsg,
                  ],
                }));
              }
              finalMessageRef.current = null;
              break;
            }
          }
        },
      );
    } catch (err: any) {
      notify(formateErrorObject(err), 'error');
    } finally {
      setProcessing(false);
      setStreamStatus(null);
      setStreamAnswer('');
    }
  };

  useEffect(() => {
    if (!open || !initialSessionId) return;

    const msgs = messagesMap[initialSessionId];

    if (Array.isArray(msgs) && msgs.length > 0) {
      setActiveChatId(initialSessionId);

      requestAnimationFrame(() => {
        bottomRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'end',
        });
      });
    }
  }, [open, initialSessionId, messagesMap]);

  const handleMessageScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;

    if (
      el.scrollTop < 80 &&
      messagesHasMoreMap[activeChatId] &&
      !loadingHistory
    ) {
      const nextPage = (messagesPageMap[activeChatId] ?? 1) + 1;
      fetchSessionMessages(activeChatId, nextPage, true);
    }
  };

  const handleSelectChat = (id: string, closeSidebar = false) => {
    setActiveChatId(id);

    if (!messagesMap[id] && !id.startsWith('new')) {
      fetchSessionMessages(id, 1);
    }

    if (closeSidebar) {
      setSidebarCollapsed(true);
    }

    if (!screens.md) setMobileSidebarOpen(false);
    bottomRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'end',
    });
  };

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    container.addEventListener('scroll', checkScrollPosition);

    // check initially
    checkScrollPosition();

    return () => {
      container.removeEventListener('scroll', checkScrollPosition);
    };
  }, [messages.length]);

  useEffect(() => {
    const timer = setTimeout(() => {
      checkScrollPosition();
    }, 50);

    return () => clearTimeout(timer);
  }, [messages, streamAnswer]);

  const ChatInputUI = (
    <div
      className={twMerge(
        'relative flex justify-center ',
        messages?.length ? 'md:mb-8 mb-1.5' : 'md:mb-8 mb-7',
      )}
    >
      <motion.div
        initial={false}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: -10, opacity: 0 }}
        transition={{ duration: 0.25, ease: 'easeOut' }}
        className='w-full 2xl:max-w-3xl xl:max-w-[640px] lg:max-w-[640px] md:max-w-[476px] max-w-[640px] md:mx-3 mx-4  relative'
      >
        <ChatInput
          messagesLength={messages?.length || 0}
          value={prompt}
          onChange={setPrompt}
          onSend={handleSend}
          disabled={processing}
        />
        <div className='absolute bottom-0 rounded-b-[32px] left-0 w-full md:h-28 h-16 bg-gradient-to-t from-grayZl-950 via-grayZl-950/40 to-transparent' />
      </motion.div>
    </div>
  );

  return (
    <>
      <Head>
        <title>ZAI</title>
      </Head>
      <motion.div
        layout
        className={clsx(
          'flex w-full bg-[#121212] text-white h-dvh xl:pt-[73px] sm:pt-[61px]  pt-[57px] ',
        )}
      >
        <div className='hidden md:block'>
          <SidebarChats
            collapsed={sidebarCollapsed}
            activeChatId={activeChatId}
            onSelectChat={(id) => handleSelectChat(id)}
            onNewChat={handleNewChat}
            setSidebarCollapsed={setSidebarCollapsed}
            chats={chats}
            setChats={setChats}
          />
        </div>

        <motion.div
          layout
          className='flex flex-1 flex-col relative justify-between'
        >
          <div
            className={twMerge(
              'z-20 relative md:pt-7 pt-2.5 md:pb-2 pb-2.5 flex items-center px-4 ',
              messages.length === 0 ? '' : 'bg-grayZl-950',
            )}
          >
            {/* <div className='z-10 absolute top-10 rounded-b-[32px] left-0 w-full h-16 bg-gradient-to-b from-grayZl-950 to-transparent' /> */}
            <ShouldRender check={!screens.md}>
              <button
                onClick={() => router.back()}
                className='rounded-full w-min text-white'
              >
                <BiChevronLeft className='h-6 w-6 text-white' />
              </button>
            </ShouldRender>
            <div
              style={{
                color:
                  'linear-gradient(270deg, #7E0D9D 0.3%, #7328F2 50%, #51A0F7 99.7%)',
              }}
              className={twMerge(
                'z-20 flex items-center gap-1.5 flex-1 justify-center ',
                messages.length === 0 ? '' : 'bg-grayZl-950',
              )}
            >
              <svg
                className='md:w-5 w-4 md:h-5 h-4'
                viewBox='0 0 17 17'
                fill='none'
                xmlns='http://www.w3.org/2000/svg'
              >
                <path
                  d='M6.2427 3.1862C7.02902 1.06122 7.42217 -0.00127041 8.11841 -0.00127041C8.81464 -0.00127041 9.2078 1.06122 9.99411 3.1862L10.5002 4.5538C10.6572 4.97815 10.7357 5.19033 10.8905 5.34511C11.0453 5.49989 11.2574 5.5784 11.6818 5.73543L13.0494 6.24148C15.1744 7.0278 16.2369 7.42095 16.2369 8.11719C16.2369 8.81342 15.1744 9.20658 13.0494 9.99289L11.6818 10.4989C11.2574 10.656 11.0453 10.7345 10.8905 10.8893C10.7357 11.044 10.6572 11.2562 10.5002 11.6806L9.99411 13.0482C9.2078 15.1732 8.81464 16.2356 8.11841 16.2356C7.42217 16.2356 7.02902 15.1732 6.2427 13.0482L5.73665 11.6806C5.57962 11.2562 5.50111 11.044 5.34633 10.8893C5.19155 10.7345 4.97937 10.656 4.55502 10.4989L3.18742 9.99289C1.06244 9.20658 -4.97103e-05 8.81342 -4.97103e-05 8.11719C-4.97103e-05 7.42095 1.06244 7.0278 3.18742 6.24148L4.55502 5.73543C4.97937 5.5784 5.19155 5.49989 5.34633 5.34511C5.50111 5.19033 5.57962 4.97815 5.73665 4.5538L6.2427 3.1862Z'
                  fill='url(#paint0_linear_18134_48087)'
                />
                <defs>
                  <linearGradient
                    id='paint0_linear_18134_48087'
                    x1='18.0582'
                    y1='8.11719'
                    x2='-1.82135'
                    y2='8.11718'
                    gradientUnits='userSpaceOnUse'
                  >
                    <stop stopColor='#7E0D9D' />
                    <stop offset='0.5' stopColor='#7328F2' />
                    <stop offset='1' stopColor='#51A0F7' />
                  </linearGradient>
                </defs>
              </svg>

              <span className='font-semibold md:text-xl text-base leading-5'>
                zAI
              </span>
            </div>

            {screens.md ? (
              // <button
              //   onClick={onClose}
              //   className='ml-auto text-grayZl-500 border border-grayZl-700 rounded-full p-1 hover:text-white'
              // >
              //   <XIcon className='h-4 w-4' />
              // </button>
              <></>
            ) : (
              <button
                onClick={() => !screens.md && setMobileSidebarOpen(true)}
                className='flex items-center ml-auto'
              >
                <BiHistory className='w-6 h-6' />
              </button>
            )}
          </div>

          <div className='flex-1 relative'>
            <RenderIf condition={messages.length === 0}>
              {/* <div className='absolute w-[503px] h-[370px] left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-[linear-gradient(180deg,#5887F6_46.26%,#7A17BE_80.68%)] opacity-[0.11] blur-[125px] rounded-full' /> */}
              <AnimatedBackground className='absolute md:w-[503px] md:h-[370px] w-[250px] h-[170px] left-1/2 top-1/2  -translate-x-1/2 -translate-y-1/2 opacity-[0.50] md:blur-[125px] blur-[75px] rounded-full' />
            </RenderIf>
            {messages.length === 0 && !loadingHistory ? (
              <div className='absolute inset-0 flex items-center justify-center'>
                <div className='flex flex-col items-center gap-0 w-full'>
                  <div className='flex flex-col items-center justify-center mb-[30px]'>
                    <div className='flex justify-center items-center w-[60px] h-[60px] md:w-[80px] md:h-[80px] rounded-full bg-[linear-gradient(270deg,#7E0D9D_0.3%,#7328F2_50%,#51A0F7_99.7%)]'>
                      <motion.div
                        initial={{ scale: 1, rotate: 0 }}
                        animate={{
                          rotate: [0, 45, 45, 180, 176, 180],
                          scale: [1, 0.6, 0.6, 1.15, 0.95, 1],
                        }}
                        transition={{
                          duration: 2.0,
                          times: [0, 0.35, 0.55, 0.8, 0.9, 1],
                          ease: 'easeInOut',
                          repeat: Infinity,
                        }}
                      >
                        <StarWithoutBg width={screens.md ? '32' : '24'} />
                      </motion.div>
                    </div>
                    <div className='text-lg font-semibold mt-6  md:w-full text-center whitespace-nowrap font-montserrat bg-gradient-to-r from-[#0063FF] via-[#BF99FF] to-[#E1ABFF] bg-clip-text text-transparent'>
                      Hello{userData?.name && `, `}{' '}
                      {userData?.name &&
                        userData?.name.split(' ').slice(0, 2).join(' ') + '!'}
                    </div>
                  </div>
                  <RenderIf
                    condition={!!screens.md}
                    fallBack={
                      <AnimatePresence>
                        <Suggestions
                          prompts={prompts}
                          onClick={(text) => {
                            setPrompt(text);
                            handleSend({ text });
                          }}
                        />
                      </AnimatePresence>
                    }
                  >
                    <div className='flex flex-wrap gap-2 justify-center max-w-4xl'>
                      {promptsLoading &&
                        Array.from({ length: 3 }).map((_, i) => (
                          <div
                            key={i}
                            className='h-7 w-44 rounded-full bg-[#1E1E1F] animate-pulse'
                          />
                        ))}

                      {!promptsLoading &&
                        prompts.map((text) => (
                          <button
                            key={text}
                            onClick={() => {
                              setPrompt(text);
                              handleSend({ text });
                            }}
                            className='px-3 py-[7px] text-sm leading-[22px] rounded-lg  border border-grayZl-800 hover:bg-[#2A2A2B] transition'
                          >
                            {text}
                          </button>
                        ))}
                    </div>
                  </RenderIf>
                  <div className='w-full mt-[62px] px-4'>{ChatInputUI}</div>
                </div>
              </div>
            ) : (
              <div
                ref={scrollContainerRef}
                onScroll={handleMessageScroll}
                className='absolute inset-0 overflow-auto overscroll-contain zai-scrollbar'
              >
                <ChatMessages
                  showSources={showSources}
                  setSourceDocumentsToDisplay={setSourceDocumentsToDisplay}
                  finalMessageRef={finalMessageRef}
                  streamStatus={streamStatus}
                  streamAnswer={streamAnswer}
                  setShowSources={setShowSources}
                  messages={messages}
                  loading={processing || loadingHistory}
                  streamSources={streamSources}
                  onFeedback={handleMessageFeedback}
                />
                <div ref={bottomRef} />
              </div>
            )}
            {showScrollDown && messages.length > 0 && (
              <button
                onClick={() =>
                  bottomRef.current?.scrollIntoView({
                    behavior: 'smooth',
                    block: 'end',
                  })
                }
                className='z-50 absolute bottom-5 left-1/2 -translate-x-1/2 h-9 w-9 rounded-full bg-[#1E1E1F]/70 border border-[#2A2A2B]/60 text-white shadow-lg hover:bg-[#2A2A2B] transition'
              >
                <ArrowDownIcon className='h-3.5 w-3.5 mx-auto' />
              </button>
            )}
          </div>
          <RenderIf condition={messages.length !== 0}>{ChatInputUI}</RenderIf>

          {/* {messages.length > 0 && (
            <motion.div
              initial={{ y: -16, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.25, ease: 'easeOut' }}
              className='pb-1'
            >
              <ChatInput
                value={prompt}
                onChange={setPrompt}
                onSend={handleSend}
                disabled={processing}
                showDisclaimer={true}
              />
            </motion.div>
          )} */}
        </motion.div>
        <RenderIf condition={!!screens.xl}>
          <Sources
            open={showSources}
            onClose={() => setShowSources(false)}
            sourceDocumentsToDisplay={sourceDocumentsToDisplay}
          />
        </RenderIf>
      </motion.div>
      <ShouldRender check={!screens.md}>
        <Drawer
          maskClosable={false}
          open={mobileSidebarOpen}
          placement='left'
          width='100%'
          closable={false}
          className='zai-drawer'
          onClose={() => setMobileSidebarOpen(false)}
          classNames={{
            mask: 'mt-[60px] bg-transparent',
            wrapper: 'mt-[60px]',
          }}
        >
          <ConfigProvider
            getPopupContainer={(node) => node?.parentElement || document.body}
          >
            <div
              className={clsx(
                // isBannerVisibleValue
                'flex w-full bg-[#121212] text-white h-full',
              )}
            >
              <SidebarChats
                collapsed={false}
                activeChatId={activeChatId}
                onSelectChat={(id) => handleSelectChat(id)}
                onNewChat={() => {
                  handleNewChat();
                  setMobileSidebarOpen(false);
                }}
                setSidebarCollapsed={setMobileSidebarOpen}
                chats={chats}
                setChats={setChats}
              />
            </div>
          </ConfigProvider>
        </Drawer>
      </ShouldRender>
      <RenderIf condition={!screens.xl}>
        <SourceDrawer
          sourceDocumentsToDisplay={sourceDocumentsToDisplay}
          open={showSources}
          onClose={() => setShowSources(false)}
        />
      </RenderIf>
    </>
  );
};

export default ZAI;
